// Keep the original renderer off the hero's critical loading path.
// Reduced-motion and data-saving visitors never download Three.js.
// Phones (<= 768 px) skip the effect: even at half resolution it blocked the main thread for seconds (NEED 938).
let bubblesStarted = false;
const bubblesAllowed = () => !window.matchMedia("(prefers-reduced-motion: reduce)").matches
  && !window.matchMedia("(max-width: 768px)").matches
  && !navigator.connection?.saveData;

function queueBubbles() {
  if (bubblesStarted || !bubblesAllowed()) return;
  if (document.hidden) {
    document.addEventListener("visibilitychange", queueBubbles, { once: true });
    return;
  }
  // Allow the fully loaded page to paint before scheduling decoration.
  window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
    const start = async () => {
      if (bubblesStarted || !bubblesAllowed()) return;
      if (document.hidden) { queueBubbles(); return; }
      bubblesStarted = true;
      try {
        const THREE = await import("https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.js");
        boot(THREE);
      } catch {
        // Decoration must not break shopping when the CDN or WebGL is unavailable.
      }
    };
    if ("requestIdleCallback" in window) window.requestIdleCallback(start, { timeout: 1500 });
    else window.setTimeout(start, 250);
  }));
}

if (bubblesAllowed()) {
  if (document.readyState === "complete") queueBubbles();
  else window.addEventListener("load", queueBubbles, { once: true });
}

function boot(THREE) {
  const mobile = window.matchMedia("(max-width: 749px)").matches;
  const maxAlive = mobile ? 28 : 66;

  const canvas = document.createElement("canvas");
  canvas.id = "kbh-bubbles";
  canvas.setAttribute("aria-hidden", "true");
  document.body.prepend(canvas);

  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: false,
    powerPreference: "low-power",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.4));
  renderer.setClearColor(0x000000, 0);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 40);
  camera.position.z = 8;

  const vertex = /* glsl */ `
    varying vec3 vNormal;
    varying vec3 vView;
    void main() {
      vNormal = normalize(normalMatrix * normal);
      vec4 mv = modelViewMatrix * vec4(position, 1.0);
      vView = normalize(-mv.xyz);
      gl_Position = projectionMatrix * mv;
    }
  `;
  const fragment = /* glsl */ `
    uniform vec3 uSoap;
    uniform vec3 uMist;
    uniform vec3 uGold;
    uniform float uAlpha;
    varying vec3 vNormal;
    varying vec3 vView;
    void main() {
      float fresnel = pow(1.0 - max(dot(vNormal, vView), 0.0), 2.6);
      float rim = smoothstep(0.28, 1.0, fresnel);
      vec3 irid = mix(uSoap, uGold, fresnel);
      irid = mix(irid, uMist, rim * 0.5);
      gl_FragColor = vec4(irid, (0.08 + rim * 0.5) * uAlpha);
    }
  `;

  const geo = new THREE.SphereGeometry(1, 12, 10);
  const ringGeo = new THREE.RingGeometry(0.8, 1.05, 20);
  const pool = [];
  const pops = [];
  const projected = new THREE.Vector3();

  function makeBubble() {
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uSoap: { value: new THREE.Color("#2e7fa8") },
        uMist: { value: new THREE.Color("#eaf4f9") },
        uGold: { value: new THREE.Color("#f3c96b") },
        uAlpha: { value: 1 },
      },
      vertexShader: vertex,
      fragmentShader: fragment,
      transparent: true,
      depthWrite: false,
      side: THREE.FrontSide,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.visible = false;
    scene.add(mesh);
    return {
      mesh, mat, alive: false, age: 0, life: 1, max: 0.1,
      vx: 0, vy: 0, vz: 0, phase: 0, f1: 0, f2: 0, lift: 0,
    };
  }
  for (let i = 0; i < maxAlive; i++) pool.push(makeBubble());

  const origin = new THREE.Vector3();
  const ndc = new THREE.Vector3();

  function originWorld() {
    const el = document.querySelector(".kbh-blow-origin");
    if (!el) return false;
    const r = el.getBoundingClientRect();
    if (r.bottom < -40 || r.top > window.innerHeight + 40) return false;
    ndc.set(
      ((r.left + r.width / 2) / window.innerWidth) * 2 - 1,
      -((r.top + r.height / 2) / window.innerHeight) * 2 + 1,
      0.9,
    );
    origin.copy(ndc).unproject(camera);
    return true;
  }

  function spawn() {
    const b = pool.find((x) => !x.alive);
    if (!b) return;
    b.alive = true;
    b.age = 0;
    b.phase = Math.random() * Math.PI * 2;
    b.f1 = 0.8 + Math.random() * 2.2;
    b.f2 = 1.1 + Math.random() * 2.8;
    b.lift = 0.4 + Math.random() * 0.7;
    b.life = 7 + Math.random() * 5;
    b.max = mobile ? 0.03 + Math.random() * 0.08 : 0.035 + Math.random() * 0.1;
    b.vx = (Math.random() - 0.5) * 0.32;
    b.vy = 0.18 + Math.random() * 0.28;
    b.vz = (Math.random() - 0.5) * 0.1;
    b.mesh.position.set(
      origin.x + (Math.random() - 0.5) * 0.28,
      origin.y + (Math.random() - 0.5) * 0.18,
      origin.z + (Math.random() - 0.5) * 0.3,
    );
    b.mesh.scale.setScalar(0.01 + Math.random() * 0.018);
    b.mat.uniforms.uAlpha.value = 0.75 + Math.random() * 0.25;
    b.mesh.visible = true;
  }

  function popAt(pos, size) {
    if (Math.random() > 0.4) return;
    const mat = new THREE.MeshBasicMaterial({
      color: 0xeaf4f9,
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(ringGeo, mat);
    mesh.position.copy(pos);
    mesh.scale.setScalar(Math.max(size, 0.04));
    scene.add(mesh);
    pops.push({ mesh, mat, age: 0 });
  }

  function screenY(mesh) {
    projected.copy(mesh.position).project(camera);
    return (-projected.y * 0.5 + 0.5) * window.innerHeight;
  }

  const resize = () => {
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
  };
  window.addEventListener("resize", resize, { passive: true });
  resize();

  let spawnAcc = 0;
  let raf = 0;
  // Mobile draws at most 30 frames per second; motion follows elapsed time, so the look stays the same.
  const minFrameMs = mobile ? 1000 / 30 : 0;
  let lastFrame = -Infinity;
  // No bubble, no pop and the blow origin off screen leave the canvas empty: stop until scroll or resize.
  let sleeping = false;
  const clock = new THREE.Clock();
  const wake = () => {
    if (!sleeping || document.hidden) return;
    sleeping = false;
    clock.getDelta();
    tick();
  };

  const tick = (now = performance.now()) => {
    raf = requestAnimationFrame(tick);
    if (now - lastFrame < minFrameMs - 1) return;
    lastFrame = now;
    const dt = Math.min(clock.getDelta(), 0.05);
    const onHero = originWorld();
    spawnAcc += dt;
    const gap = mobile ? 0.16 : 0.09;
    if (onHero) {
      while (spawnAcc > gap) {
        spawnAcc -= gap;
        spawn();
        if (Math.random() < 0.4) spawn();
      }
    } else spawnAcc = 0;

    const floor = window.innerHeight - 18;

    for (const b of pool) {
      if (!b.alive) continue;
      b.age += dt;
      const t = b.age / b.life;
      if (t >= 1 || screenY(b.mesh) > floor) {
        popAt(b.mesh.position, b.mesh.scale.x);
        b.alive = false;
        b.mesh.visible = false;
        continue;
      }
      const grow = t < 0.55 ? Math.pow(t / 0.55, 0.55) : 1;
      b.mesh.scale.setScalar(0.012 + b.max * grow);
      b.mat.uniforms.uAlpha.value = t > 0.92 ? 1 - (t - 0.92) / 0.08 : 1;

      if (b.age < b.lift) {
        b.vy += 0.06 * dt;
      } else {
        b.vy -= 0.22 * dt;
      }
      b.vx += Math.sin(b.age * b.f1 + b.phase) * 0.22 * dt;
      b.vx += Math.sin(b.age * b.f2 + b.phase * 0.5) * 0.12 * dt;
      b.vz += Math.sin(b.age * b.f1 * 0.7) * 0.04 * dt;
      b.mesh.position.x += b.vx * dt;
      b.mesh.position.y += b.vy * dt;
      b.mesh.position.z += b.vz * dt;
    }

    for (let i = pops.length - 1; i >= 0; i--) {
      const p = pops[i];
      p.age += dt;
      const k = p.age / 0.22;
      p.mesh.scale.setScalar(p.mesh.scale.x + dt * 1.4);
      p.mat.opacity = Math.max(0, 0.4 * (1 - k));
      if (k >= 1) {
        scene.remove(p.mesh);
        p.mat.dispose();
        pops.splice(i, 1);
      }
    }

    renderer.render(scene, camera);
    if (!onHero && !pops.length && !pool.some((b) => b.alive)) {
      cancelAnimationFrame(raf);
      sleeping = true;
    }
  };
  tick();
  window.addEventListener("scroll", wake, { passive: true });
  window.addEventListener("resize", wake, { passive: true });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) cancelAnimationFrame(raf);
    else if (!sleeping) {
      clock.getDelta();
      tick();
    }
  });
}
